/*
 * Copyright (c) Castle Point Learning Systems Corporation, 2013-2023, all rights reserved.
 * NOTICE: Dissemination of the information and/or materials contained in this file is strictly
 * forbidden unless prior written permission is obtained from Castle Point Learning Systems Corporation.
 * See LICENSE.txt (part of this package) for the full notice.
 */

import { useCallback, useContext } from 'react';
import { EvaluationContext } from '../context/EvaluationContextProvider';
import { calculateParts, findCurrentPartIndex, getNearestNode, getNodeIdsNearCursor, getSelectionBoundaryPoint } from '../utils/evaluationUtils';
import { EvaluationLine } from '../../../types/model';
import { NotifyObject } from 'semantic-math-editor';

const moveToAnotherLine = (from: EvaluationLine, to: EvaluationLine) => {
    const fromEditor = from.editor;
    const toEditor = to.editor;
    const lineCursor = getNearestNode(toEditor, getSelectionBoundaryPoint(fromEditor));

    toEditor.placeCursor(lineCursor.nodeId, lineCursor.side);
    toEditor.focus();
    const parts = calculateParts(to.tree);

    return {
        activeLine: { lineId: to.id, activePartIndex: findCurrentPartIndex(to), partsCount: parts.length },
        nodeIdsNearCursor: getNodeIdsNearCursor(toEditor),
    };
};

export const useEvaluationNavigation = (lineId: string) => {
    const { evaluation, dispatchEvaluation } = useContext(EvaluationContext);

    return useCallback(
        (direction?: NotifyObject) => {
            const line = evaluation.lines.find((l) => l.id === lineId);
            const lines = evaluation.lines;

            if (direction) {
                let cursor;

                const lineIndex = lines.indexOf(line);
                if (direction === NotifyObject.failedNavigationUp && lineIndex > 0) {
                    cursor = moveToAnotherLine(line, lines[lineIndex - 1]);
                }

                if (direction === NotifyObject.failedNavigationDown && lineIndex < lines.length - 1) {
                    cursor = moveToAnotherLine(line, lines[lineIndex + 1]);
                }

                if (cursor) {
                    dispatchEvaluation((prevState) => ({
                        ...prevState,
                        activeLine: cursor.activeLine,
                        nodeIdsNearCursor: cursor.nodeIdsNearCursor,
                    }));
                }
                return;
            }

            const newPartIndex = findCurrentPartIndex(line);
            const activePartIndex = evaluation.activeLine.activePartIndex;
            const newEvaluationPosition = activePartIndex !== newPartIndex || line.id !== evaluation.activeLine.lineId;

            dispatchEvaluation((prevState) => ({
                ...prevState,
                nodeIdsNearCursor: getNodeIdsNearCursor(line.editor),
                activeLine: newEvaluationPosition
                    ? {
                          lineId: line.id,
                          activePartIndex: newPartIndex,
                          partsCount: prevState.activeLine.partsCount,
                      }
                    : prevState.activeLine,
            }));
        },
        [evaluation, dispatchEvaluation, lineId]
    );
};
